// Redux Utils
import { useDispatch, useSelector } from "react-redux";
import { confirmUser } from "@/features/confirm/confirmThunks";
import { resetConfirmation } from "@/features/confirm/confirmSlice";

// React Utils
import React, { useState, useEffect } from "react";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";
import AppWhoopLogo from "@/assets/appwhoop_ticket_logo.webp";
import ValidateCode from "@/components/ValidateCode";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useSearchParams } from "react-router-dom";

export default function ConfirmarUsuario() {
  // Redux
  const dispatch = useDispatch();
  const { confirmed, error } = useSelector((state) => state.confirm);
  const [searchParams] = useSearchParams();
  const codeFromUrl = searchParams.get("code") ?? "";
  const emailFromUrl = searchParams.get("email") ?? "";
  console.log("confirmed:", confirmed);
  console.log("error:", error);

  // Estados del formulario
  const [otpCodeisComplete, setOtpCodeisComplete] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    email: emailFromUrl,
    confirmationCode: "",
  });

  // Validación del formulario
  const validate = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = "El correo electrónico es obligatorio";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "El formato del correo electrónico no es válido";
    } else if (formData.email.length > 50) {
      newErrors.email = "El correo no puede tener más de 50 caracteres";
    }

    if (!otpCode || otpCode.length !== 6) {
      newErrors.confirmationCode =
        "El código de confirmación debe tener 6 dígitos";
    }

    // Error específico cuando el código está completo pero falta el email
    if (otpCode.length === 6 && !formData.email.trim()) {
      newErrors.email =
        "Por favor, ingresa tu correo electrónico para continuar";
    }

    return newErrors;
  };

  // Maneja la finalización del código OTP
  const handleOtpComplete = (code) => {
    setFormData({ ...formData, confirmationCode: code });
    // Limpiar errores del código cuando se complete
    if (errors.confirmationCode && code.length === 6) {
      setErrors((prev) => ({ ...prev, confirmationCode: "" }));
    }
  };

  // Maneja cambios en el input de email
  const handleEmailChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, email: value });

    // Limpiar errores de email cuando el usuario esté escribiendo
    if (errors.email && value.trim()) {
      setErrors((prev) => ({ ...prev, email: "" }));
    }
  };

  // Maneja el envío del formulario
  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = validate();
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      // Aquí puedes manejar el envío del formulario
      dispatch(confirmUser({ email: formData.email, code: otpCode }));
      console.log("Formulario enviado:", formData);
    } else {
      console.log("Errores de validación:", newErrors);
    }
  };

  // Verifica si el código OTP y el correo electrónico están completos
  useEffect(() => {
    if (otpCode.length === 6 && formData.email.trim() !== "") {
      setOtpCodeisComplete(true);
    } else {
      setOtpCodeisComplete(false);
    }

    // Validar en tiempo real cuando el código esté completo pero falte email
    if (otpCode.length === 6 && !formData.email.trim()) {
      setErrors((prev) => ({
        ...prev,
        email: "Por favor, ingresa tu correo electrónico para continuar",
      }));
    }
  }, [otpCode, formData.email]);

  return (
    <main
      id="app-content"
      className="flex flex-col items-center justify-center min-h-screen bg-aw-bgbluedark"
    >
      <div
        id="bck-cooming-soon"
        className="flex flex-col items-center justify-center w-full min-h-screen"
      >
        <div className="relative flex flex-col justify-center gap-y-3 p-4 md:w-2/3 2xl:w-3/7 h-[80dvh] bg-aw-bluedark-75 rounded-lg shadow-lg">
          <Link
            to="/"
            className="absolute top-4 left-4 text-white hover:text-aw-green transition-colors duration-200"
          >
            <FontAwesomeIcon icon="arrow-left" className="mr-2" />
            Volver al Home
          </Link>
          <Link
            onClick={() => dispatch(resetConfirmation())}
            to="/reenviar-codigo-confirmar-usuario"
            className="group absolute top-4 right-4 text-white hover:text-amber-400 transition-colors duration-200"
          >
            <FontAwesomeIcon
              icon="fa-solid fa-rotate"
              className="group-hover:animate-spin mr-2"
            />
            Volver a Intentar
          </Link>
          <div className="flex items-center justify-center">
            <LazyLoadImage
              alt="AppWhoop Logo"
              effect="blur"
              wrapperProps={{
                // If you need to, you can tweak the effect transition using the wrapper style.
                style: { transitionDelay: "0.3s" },
              }}
              src={AppWhoopLogo}
              className="w-4/5 sm:w-1/2 xl:w-2/5 object-contain mx-auto"
            />
          </div>

          <div className="flex flex-col text-center items-center justify-center">
            {/* Formulario para ingresar correo y código de confirmación para activar usuario */}
            {confirmed == false && error == null ? (
              <>
                <div className="flex flex-col text-center items-center justify-center text-white">
                  <h2 className="text-xl sm:text-3xl font-bold text-aw-green">
                    Confirma tu usuario
                  </h2>
                  <p className="text-sm text-white">
                    Ingresa tu correo electrónico y el código de verificación
                    que te enviamos para activar tu cuenta.
                  </p>
                </div>
                <form
                  onSubmit={(e) => {
                    handleSubmit(e);
                  }}
                  className="w-full max-w-md text-white rounded-lg p-4 shadow-md"
                >
                  <div className="mb-12">
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleEmailChange}
                      className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 text-white placeholder:text-gray-400 ${
                        errors.email
                          ? "border-red-500 focus:ring-red-500"
                          : "border-gray-300 focus:ring-aw-bluedark"
                      }`}
                      placeholder="Ingresa tu correo electrónico"
                    />
                    {errors.email && (
                      <span className="text-xs text-red-400 mt-1 block">
                        {errors.email}
                      </span>
                    )}
                  </div>
                  <div className="mb-4 mx-auto">
                    <label
                      htmlFor="confirmationCode"
                      className="block text-sm font-medium mb-1"
                    >
                      Código de Confirmación
                    </label>
                    <ValidateCode
                      length={6}
                      onComplete={handleOtpComplete}
                      verifyQuantity={setOtpCode}
                      prefillCode={codeFromUrl}
                    />
                    {errors.confirmationCode && (
                      <span className="text-xs text-red-400 mt-1 block text-center">
                        {errors.confirmationCode}
                      </span>
                    )}
                  </div>
                  <button
                    type="submit"
                    disabled={!otpCodeisComplete}
                    className="w-full py-2 px-4 disabled:bg-gray-400 disabled:hover:!cursor-not-allowed text-aw-dark font-semibold bg-aw-green rounded-md duration-200"
                  >
                    Confirmar Usuario
                  </button>
                </form>
              </>
            ) : error != null ? (
              <div className="w-full max-w-md text-white rounded-lg p-4 shadow-md">
                <FontAwesomeIcon
                  icon="exclamation-circle"
                  size="2xl"
                  className="block text-red-400 mb-2"
                />
                <p className="text-center text-red-400 font-semibold">
                  {error.message}
                </p>
                <Link
                  onClick={() => dispatch(resetConfirmation())}
                  to="/reenviar-codigo-confirmar-usuario"
                  className="bg-aw-green text-aw-bluedark px-4 py-2 font-semibold hover:scale-95 rounded-md mt-4 inline-block text-center transition-scale duration-200"
                >
                  Reenviar Código de Confirmación
                </Link>
              </div>
            ) : (
              <div className="w-full max-w-md text-white rounded-lg p-4 shadow-md">
                <p className="text-center text-green-400 font-semibold">
                  ¡Usuario confirmado con éxito! Ya puedes iniciar sesión.
                </p>
                <Link
                  onClick={() => dispatch(resetConfirmation())}
                  to="/"
                  className="bg-aw-green text-aw-bluedark px-4 py-2 font-semibold hover:scale-95 rounded-md mt-4 inline-block text-center transition-scale duration-200"
                >
                  Regresar al Home
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
